import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Table, Button, 
  Modal, Form, Alert, Spinner, Badge 
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI, academicYearsAPI } from '../../services/api';

const AcademicYears = () => {
  const [user, setUser] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [selectedYear, setSelectedYear] = useState(null);
  const [formData, setFormData] = useState({
    academic_year: '',
    status: 0
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    setUser(currentUser);
    fetchAcademicYears();
  }, [navigate]);

  const fetchAcademicYears = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await academicYearsAPI.getAll();
      
      if (result && result.success === true) {
        setAcademicYears(result.data || []);
      } else {
        setError(result?.message || 'Failed to fetch academic years');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const handleAddClick = () => {
    setModalType('add');
    setFormData({
      academic_year: '',
      status: 0
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditClick = (year) => {
    setModalType('edit');
    setSelectedYear(year);
    setFormData({
      academic_year: year.academic_year,
      status: year.status
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteClick = (year) => {
    setDeleteConfirm(year);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const result = await academicYearsAPI.delete(deleteConfirm.id);
      
      if (result && result.success === true) {
        setSuccess('Academic year deleted successfully');
        fetchAcademicYears();
      } else {
        setError(result?.message || 'Failed to delete academic year');
      }
    } catch (error) {
      setError(error.message || 'Delete failed');
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handleSetActive = async (id) => {
    try {
      setLoading(true);
      // Find the year to toggle
      const yearToUpdate = academicYears.find(y => y.id === id);
      if (!yearToUpdate) return;
      
      // Toggle status: if active -> inactive, if inactive -> active
      const newStatus = yearToUpdate.status === 1 ? 0 : 1;
      
      const result = await academicYearsAPI.update(id, {
        academic_year: yearToUpdate.academic_year,
        status: newStatus
      });
      
      if (result && result.success === true) {
        setSuccess(`Academic year ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        fetchAcademicYears();
      } else {
        setError(result?.message || 'Failed to update academic year status');
      }
    } catch (error) {
      setError(error.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.academic_year.trim()) {
      errors.academic_year = 'Academic year is required';
    } else if (!/^\d{4}-\d{4}$/.test(formData.academic_year)) {
      errors.academic_year = 'Format should be YYYY-YYYY (e.g., 2024-2025)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      let result;
      
      if (modalType === 'add') {
        result = await academicYearsAPI.create(formData);
      } else {
        result = await academicYearsAPI.update(selectedYear.id, formData);
      }
      
      if (result && result.success === true) {
        setSuccess(`Academic year ${modalType === 'add' ? 'added' : 'updated'} successfully`);
        setShowModal(false);
        fetchAcademicYears();
      } else {
        setError(result?.message || `Failed to ${modalType} academic year`);
      }
    } catch (error) {
      setError(error.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Get status label
  const getStatusLabel = (year) => {
    return year.status === 1 ? 'Active' : 'Inactive';
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    return status === 1 ? 'success' : 'danger'; // Green for Active, Red for Inactive
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">
              <i className="fas fa-calendar-alt me-2 text-primary"></i>
              Academic Years Management
            </h2>
            <p className="text-muted mb-0">Manage academic years for the college</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={handleAddClick}>
              <i className="fas fa-plus me-2"></i>
              Add New Academic Year
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={fetchAcademicYears}
              className="ms-2"
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </Button>
          </Col>
        </Row>

        {/* Messages */}
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Stats Card */}
        <Row className="mb-4">
          <Col lg={3}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-calendar-alt fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{academicYears.length}</h3>
                    <p className="mb-0">Total Academic Years</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-toggle-on fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">
                      {academicYears.filter(y => y.status === 1).length}
                    </h3>
                    <p className="mb-0">Active Years</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-warning text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-toggle-off fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">
                      {academicYears.filter(y => y.status === 0).length}
                    </h3>
                    <p className="mb-0">Inactive Years</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Academic Years Table */}
        <Card className="shadow">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading academic years...</p>
              </div>
            ) : academicYears.length > 0 ? (
              <div className="table-responsive">
                <Table hover striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Academic Year</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {academicYears.map((year) => (
                      <tr key={year.id}>
                        <td>{year.id}</td>
                        <td>
                          <strong>{year.academic_year}</strong>
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeColor(year.status)} className="px-3 py-2">
                            <i className={`fas fa-${year.status === 1 ? 'check-circle' : 'times-circle'} me-1`}></i>
                            {getStatusLabel(year)}
                          </Badge>
                        </td>
                        <td>
                          {new Date(year.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button 
                              variant={year.status === 1 ? 'outline-warning' : 'outline-success'} 
                              size="sm"
                              onClick={() => handleSetActive(year.id)}
                              disabled={loading}
                              title={year.status === 1 ? "Set as Inactive" : "Set as Active"}
                            >
                              <i className={`fas ${year.status === 1 ? 'fa-times' : 'fa-check'} me-1`}></i>
                              {year.status === 1 ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleEditClick(year)}
                              disabled={loading}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Edit
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteClick(year)}
                              disabled={loading}
                            >
                              <i className="fas fa-trash me-1"></i>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                <h5>No academic years found</h5>
                <p className="text-muted mb-4">Add your first academic year to get started</p>
                <Button variant="primary" onClick={handleAddClick}>
                  <i className="fas fa-plus me-2"></i>
                  Add Academic Year
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-alt me-2"></i>
            {modalType === 'add' ? 'Add New Academic Year' : 'Edit Academic Year'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Academic Year <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="academic_year"
                placeholder="e.g., 2024-2025"
                value={formData.academic_year}
                onChange={handleInputChange}
                isInvalid={!!formErrors.academic_year}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.academic_year}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Format: YYYY-YYYY (e.g., 2024-2025)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="status"
                label={
                  <span>
                    Active Academic Year 
                    <Badge bg={formData.status === 1 ? 'success' : 'danger'} className="ms-2">
                      {formData.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </span>
                }
                checked={formData.status === 1}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Check to set this academic year as active (Green), uncheck for inactive (Red)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalType === 'add' ? 'Adding...' : 'Updating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  {modalType === 'add' ? 'Add Year' : 'Update Year'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the academic year:</p>
          <h5 className="text-center my-3">
            <Badge bg="secondary" className="p-2 fs-5">{deleteConfirm?.academic_year}</Badge>
            <div className="mt-2">
              <Badge bg={getStatusBadgeColor(deleteConfirm?.status)} className="px-3 py-1">
                {deleteConfirm?.status === 1 ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </h5>
          <p className="text-danger small">
            <i className="fas fa-exclamation-circle me-1"></i>
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete} 
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AcademicYears;